/**
 * Pure text edits behind the Markdown toolbar. Each action takes the textarea
 * value and selection and returns the new value and selection, so the caller
 * only has to put them back (no WYSIWYG, the Markdown stays plain text).
 *
 * - inline actions (bold, italic, code, link) wrap the selection, or insert a
 *   template with its placeholder selected; applying bold again to an
 *   already-bold selection removes the markers;
 * - line actions (lists, quote, heading) work on every line the selection
 *   touches, and toggle off when all of them already have the prefix.
 */
export type MarkdownAction =
  | 'bold'
  | 'italic'
  | 'strikethrough'
  | 'unorderedList'
  | 'orderedList'
  | 'checklist'
  | 'heading'
  | 'link'
  | 'quote'
  | 'code'
  | 'codeBlock'
  | 'rule';

export interface TextEdit {
  value: string;
  selectionStart: number;
  selectionEnd: number;
}

export interface FormatOptions {
  /** Heading level 1-6 (for `heading`). */
  level?: number;
  /** Placeholder texts (localized by the caller). */
  placeholders?: { text?: string; url?: string; code?: string };
}

const DEFAULT_PLACEHOLDERS = { text: 'text', url: 'https://', code: 'code' };

/** Length of the run of `char` ending at `index` (exclusive) going left / starting at it going right. */
function runLeft(value: string, index: number, char: string): number {
  let n = 0;
  while (index - n - 1 >= 0 && value[index - n - 1] === char) n++;
  return n;
}
function runRight(value: string, index: number, char: string): number {
  let n = 0;
  while (value[index + n] === char) n++;
  return n;
}

/**
 * Asterisks nest: in `**word**` the selection is bold but NOT italic, in `***word***` it is both.
 * Whether the selection already has `before` as its own marker therefore depends on the whole run
 * of asterisks around it: italic needs an odd run, bold at least two.
 */
function markersMatch(value: string, start: number, end: number, before: string): boolean {
  if (before !== '*' && before !== '**') return true;
  const run = Math.min(runLeft(value, start, '*'), runRight(value, end, '*'));
  return before === '*' ? run % 2 === 1 : run >= 2;
}

function wrap(
  value: string,
  start: number,
  end: number,
  before: string,
  after: string,
  placeholder: string,
): TextEdit {
  const selected = value.slice(start, end);
  const wrapped =
    selected !== '' &&
    value.slice(start - before.length, start) === before &&
    value.slice(end, end + after.length) === after &&
    markersMatch(value, start, end, before);
  if (wrapped) {
    return {
      value: value.slice(0, start - before.length) + selected + value.slice(end + after.length),
      selectionStart: start - before.length,
      selectionEnd: end - before.length,
    };
  }
  const inner = selected || placeholder;
  return {
    value: value.slice(0, start) + before + inner + after + value.slice(end),
    selectionStart: start + before.length,
    selectionEnd: start + before.length + inner.length,
  };
}

/** The lines touched by the selection: [blockStart, blockEnd) in `value`. */
function lineBlock(value: string, start: number, end: number): [number, number] {
  const blockStart = value.lastIndexOf('\n', start - 1) + 1;
  // A selection ending right after a newline does not include the next line.
  const probe = end > start && value[end - 1] === '\n' ? end - 1 : end;
  const next = value.indexOf('\n', probe);
  return [blockStart, next === -1 ? value.length : next];
}

const HEADING_RE = /^#{1,6} /;

function transformLines(
  value: string,
  start: number,
  end: number,
  transform: (lines: string[]) => string[],
): TextEdit {
  const [blockStart, blockEnd] = lineBlock(value, start, end);
  const lines = transform(value.slice(blockStart, blockEnd).split('\n'));
  const block = lines.join('\n');
  const next = value.slice(0, blockStart) + block + value.slice(blockEnd);
  // Keep the cursor after the prefix on an empty single line, else select the block.
  const collapsed = start === end && lines.length === 1;
  return {
    value: next,
    selectionStart: collapsed ? blockStart + block.length : blockStart,
    selectionEnd: blockStart + block.length,
  };
}

function togglePrefix(prefix: string): (lines: string[]) => string[] {
  return (lines) =>
    lines.every((line) => line.startsWith(prefix))
      ? lines.map((line) => line.slice(prefix.length))
      : lines.map((line) => (line.startsWith(prefix) ? line : prefix + line));
}

const ORDERED_RE = /^\d+\. /;
const CHECK_RE = /^- \[[ xX]\] /;
const BULLET_RE = /^- /;

/** `- [ ] ` on every line; applied again to all-checklist lines it takes the checklist away. */
function toggleChecklist(lines: string[]): string[] {
  return lines.every((line) => CHECK_RE.test(line))
    ? lines.map((line) => line.replace(CHECK_RE, ''))
    : lines.map((line) => `- [ ] ${line.replace(CHECK_RE, '').replace(BULLET_RE, '')}`);
}

function toggleOrdered(lines: string[]): string[] {
  return lines.every((line) => ORDERED_RE.test(line))
    ? lines.map((line) => line.replace(ORDERED_RE, ''))
    : lines.map((line, index) => `${index + 1}. ${line.replace(ORDERED_RE, '')}`);
}

function setHeading(level: number): (lines: string[]) => string[] {
  const prefix = `${'#'.repeat(level)} `;
  return (lines) =>
    lines.every((line) => line.startsWith(prefix))
      ? lines.map((line) => line.slice(prefix.length))
      : lines.map((line) => prefix + line.replace(HEADING_RE, ''));
}

export function applyMarkdownFormat(
  action: MarkdownAction,
  value: string,
  selectionStart: number,
  selectionEnd: number,
  options: FormatOptions = {},
): TextEdit {
  const placeholders = { ...DEFAULT_PLACEHOLDERS, ...options.placeholders };
  const start = Math.min(selectionStart, selectionEnd);
  const end = Math.max(selectionStart, selectionEnd);
  switch (action) {
    case 'bold':
      return wrap(value, start, end, '**', '**', placeholders.text);
    case 'italic':
      return wrap(value, start, end, '*', '*', placeholders.text);
    case 'strikethrough':
      return wrap(value, start, end, '~~', '~~', placeholders.text);
    case 'code':
      return wrap(value, start, end, '`', '`', placeholders.code);
    case 'rule': {
      // On a line of its own with blank lines around it: right under a text line `---` would turn
      // that line into a heading. Inserted after the selection, so no text is ever replaced.
      const head = value.slice(0, end);
      const tail = value.slice(end);
      const lead =
        head === '' ? '' : head.endsWith('\n\n') ? '' : head.endsWith('\n') ? '\n' : '\n\n';
      const trail =
        tail === '' ? '\n' : tail.startsWith('\n\n') ? '' : tail.startsWith('\n') ? '\n' : '\n\n';
      const insert = `${lead}---${trail}`;
      const caret = end + insert.length;
      return { value: head + insert + tail, selectionStart: caret, selectionEnd: caret };
    }
    case 'link': {
      const selected = value.slice(start, end);
      const text = selected || placeholders.text;
      const url = placeholders.url;
      const next = `${value.slice(0, start)}[${text}](${url})${value.slice(end)}`;
      // With selected text, the URL is what the user types next; otherwise the link text.
      return selected
        ? {
            value: next,
            selectionStart: start + text.length + 3,
            selectionEnd: start + text.length + 3 + url.length,
          }
        : { value: next, selectionStart: start + 1, selectionEnd: start + 1 + text.length };
    }
    case 'codeBlock': {
      const selected = value.slice(start, end) || placeholders.code;
      const lead = start === 0 || value[start - 1] === '\n' ? '' : '\n';
      const trail = end >= value.length || value[end] === '\n' ? '' : '\n';
      const before = `${lead}\`\`\`\n`;
      return {
        value: `${value.slice(0, start)}${before}${selected}\n\`\`\`${trail}${value.slice(end)}`,
        selectionStart: start + before.length,
        selectionEnd: start + before.length + selected.length,
      };
    }
    case 'unorderedList':
      return transformLines(value, start, end, togglePrefix('- '));
    case 'orderedList':
      return transformLines(value, start, end, toggleOrdered);
    case 'checklist':
      return transformLines(value, start, end, toggleChecklist);
    case 'quote':
      return transformLines(value, start, end, togglePrefix('> '));
    case 'heading':
      return transformLines(value, start, end, setHeading(options.level ?? 2));
  }
}

/** The inline formatting shortcut of a key press (Ctrl/Cmd + B, I, K, E), if it is one. */
export function markdownShortcut(event: {
  key: string;
  ctrlKey: boolean;
  metaKey: boolean;
  altKey: boolean;
  shiftKey: boolean;
}): MarkdownAction | null {
  if (!(event.ctrlKey || event.metaKey) || event.altKey || event.shiftKey) return null;
  switch (event.key.toLowerCase()) {
    case 'b':
      return 'bold';
    case 'i':
      return 'italic';
    case 'k':
      return 'link';
    case 'e':
      return 'code';
    default:
      return null;
  }
}

/**
 * Which toolbar actions already apply to the selection (for `aria-pressed`): an inline mark
 * wrapped around a non-empty selection, or a line prefix on every touched line. Only what can be
 * told from the text is reported; an empty selection reports line formats only.
 */
export function activeFormats(
  value: string,
  selectionStart: number,
  selectionEnd: number,
): Set<MarkdownAction> {
  const start = Math.min(selectionStart, selectionEnd);
  const end = Math.max(selectionStart, selectionEnd);
  const active = new Set<MarkdownAction>();
  const around = (before: string, after = before) =>
    start < end &&
    value.slice(start - before.length, start) === before &&
    value.slice(end, end + after.length) === after &&
    markersMatch(value, start, end, before);
  if (around('**')) active.add('bold');
  if (around('*')) active.add('italic');
  if (around('~~')) active.add('strikethrough');
  if (around('`')) active.add('code');
  const [blockStart, blockEnd] = lineBlock(value, start, end);
  const lines = value.slice(blockStart, blockEnd).split('\n');
  const all = (test: (line: string) => boolean) => lines.every(test);
  if (all((line) => CHECK_RE.test(line))) active.add('checklist');
  else if (all((line) => BULLET_RE.test(line))) active.add('unorderedList');
  if (all((line) => ORDERED_RE.test(line))) active.add('orderedList');
  if (all((line) => line.startsWith('> '))) active.add('quote');
  if (all((line) => HEADING_RE.test(line))) active.add('heading');
  return active;
}
